// declare a new angular service EinvValidatorService

import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { EINVOICE_AP_TINTYPE, EINVOICE_TINTYPE } from '@core';
import { _HttpClient } from '@delon/theme';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { EinvControlType } from 'src/app/components/einvoice/einv-control/einv-control.component';

export function conditionalRequiredValidator(
  stockIdControl: AbstractControl,
  einvControl: number
): ValidatorFn {
  return (
    classificationControl: AbstractControl
  ): { [key: string]: any } | null => {
    if (stockIdControl.value && !classificationControl.value) {
      return { required: true }; // Validation error if classification is empty
    }
    if (stockIdControl.value && classificationControl.value === '004' && (einvControl === 1 || einvControl === 2)) {
      if (einvControl === 1) // E-Invoice Portal
        return {
          disallowedValue: {
            message:
              'Please select a classification code other than 004 - Consolidated e-Invoice to ensure successful validation of your customers\' e-Invoice submissions on MyInvois.',
          },
        };
      else if (einvControl === 2) // Full Validation
        return {
          disallowedValue: {
            message:
              'Classification Code 004 - Consolidated e-Invoice is intended solely for consolidated e-invoices and must not be used for individual transaction e-invoice submissions.',
          },
        };
    }
    return null; // No error
  };
}

export function tinValidator(
  idTypeValue: string,
  idValue: AbstractControl,
  http: _HttpClient
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null); // No error
    }

    if (control.status === 'DISABLED') return of(null); // No error

    const validateBody = {
      idType: idTypeValue,
      idValue: idValue.value,
      tin: control.value
    };

    // No error if information is not complete
    if (!validateBody.idType || !validateBody.idValue) return of(null);

    return http
      .post('api/einvoices/validateTin', validateBody, { _raw: true })
      .pipe(
        map(response => {
          if (response.data) return null; // No error
          let errMsg = `TIN (<b class="text-red">${validateBody.tin}</b>) and identity(<b class="text-red">${validateBody.idType} - ${validateBody.idValue}</b>) combination is invalid.`;
          if (validateBody.idType === 'BRN')
            errMsg = `TIN (<b class="text-red">${validateBody.tin}</b>) is invalid.`;
          return { invalidTin: { message: errMsg } }; // Handle validation result
        }),
        catchError(error => {
          // console.error('TIN validation error:', error); // Log the error for debugging
          return of(null); // Return null to avoid validation error due to HTTP issues
        })
      );
  };
}

@Injectable({ providedIn: 'root' })
export class EinvValidatorsService {
  private http = inject(_HttpClient);
  public toggleFormValidators(
    form: FormGroup | any,
    einvControl: EinvControlType | any
  ) {
    if (!form || einvControl === undefined || einvControl === null) return;

    const isSelfBilled = form.get('isSelfBilled')?.value;

    let headerRequiredFields: string[] = [];
    if (isSelfBilled) {
      headerRequiredFields = [
        'supplierName',
        'billingFrom',
        'cnFrom',
        'dnFrom',
        'tinType',
        'registrationNo',
        'registrationNumber',
        'tinNumber',
        'tinNo',
        'country',
        'state',
        'city',
        'billingAddress',
        'phoneNo'
        //'phone1'
      ];
    } else {
      headerRequiredFields = [
        'customerName',
        'customerTinType',
        'registrationNo',
        'registrationNumber',
        'tinNumber',
        'tinNo',
        'country',
        'state',
        'city',
        'billingAddress',
        'customerPhone',
        'phoneNo'
      ];
    }

    // Toggle required fields for header, only full validation requires all fields
    headerRequiredFields.forEach(field => {
      this.toggleControlRequired(form, field, einvControl === 2);
    });

    // Setup TIN http validator
    const tinNumberControl = form.get('tinNumber');
    if (tinNumberControl) {
      this.toggleTinValidator(isSelfBilled, form, einvControl === 2);
    }

    // If the form has form array lineItems, setup stock classification validators for each line
    if ('itemDetails' in form.controls) {
      const lineItems = form.get('itemDetails') as FormArray;
      lineItems.controls.forEach((line, index) => {
        this.toggleLineValidators(line, einvControl);
      });
    }
  }

  public toggleLineValidators(
    line: FormGroup | any,
    einvControl: EinvControlType | any
  ) {
    // Classifcation is required when control is E-Invoice Portal(1) or Full Validation(2)
    this.toggleClassificationRequired(
      line,
      einvControl
    );
  }

  private toggleTinValidator(
    selfBilled: boolean,
    form: FormGroup,
    required: boolean
  ) {
    const tinTypeControl = form.get('tinType') ?? form.get('customerTinType');

    const regNoControl =
      form.get('registrationNumber') ?? form.get('registrationNo');

    if (!tinTypeControl || !regNoControl) return;

    const tinNumberControl = form.get('tinNumber');
    if (!tinNumberControl) return;

    // If the stockId is empty, the classification is not required
    if (!required) {
      tinNumberControl.clearAsyncValidators();
      tinNumberControl.updateValueAndValidity({ onlySelf: true });
      return;
    }

    // TIN LHDN validate API disabled — previously blocked save when TIN/identity did not pass Full Validation
    // const idTypeValue = selfBilled
    //   ? EINVOICE_AP_TINTYPE.find(x => x.value === tinTypeControl.value)
    //       ?.idType ?? 'BRN'
    //   : EINVOICE_TINTYPE.find(x => x.value === tinTypeControl.value)?.idType ??
    //     'BRN';
    // tinNumberControl.setAsyncValidators([
    //   tinValidator(idTypeValue, regNoControl, this.http)
    // ]);
    // tinNumberControl.updateValueAndValidity({ onlySelf: true });
    tinNumberControl.clearAsyncValidators();
    tinNumberControl.updateValueAndValidity({ onlySelf: true });
  }

  private toggleClassificationRequired(form: FormGroup, einvControl: number) {
    const stockIdControl = form.get('stockId');
    if (!stockIdControl) return;

    const classificationControl = form.get('classification');
    if (!classificationControl) return;

    const required = einvControl === 1 || einvControl === 2;
    // If the stockId is empty, the classification is not required
    if (!required) {
      classificationControl.setErrors(null);
      classificationControl.clearValidators();
      classificationControl.updateValueAndValidity({ onlySelf: true });
      return;
    }

    // Set conditional required validator for classification
    classificationControl.setValidators([
      conditionalRequiredValidator(stockIdControl, einvControl)
    ]);
    classificationControl.updateValueAndValidity({ onlySelf: true });
  }

  private toggleRequiredField(
    form: FormGroup,
    controlName: string,
    required: boolean
  ) {
    const control = form.get(controlName);
    if (!control) return;
    this.toggleRequired(controlName, control, required);
  }

  private toggleControlRequired(
    form: FormGroup,
    controlName: string,
    required: boolean
  ) {
    const control = form.get(controlName);
    if (!control) return;

    this.toggleRequired(controlName, control, required);
  }

  private toggleRequired(
    key: string,
    control: AbstractControl | any,
    required: boolean
  ) {
    // // Get the current validators of the control
    // let validators = control.validator ? [control.validator] : [];

    // if (required) {
    //   // If the control is required, check if the control already has the required validator, if not, add the required validator to the control
    //   if (!validators.includes(Validators.required)) {
    //     validators.push(Validators.required);
    //   }
    // } else {
    //   // If the control is not required, remove the required validator from the control
    //   const index = validators.indexOf(Validators.required);
    //   if (index >= 0) {
    //     validators.splice(index, 1);
    //   }
    // }

    const validators = this.getValidators(key, required);

    // Clear the current error of the control
    control.setErrors(null);
    control.setValidators([Validators.nullValidator]);
    control.setValidators(validators);

    // reset validation errors
    control.updateValueAndValidity({ onlySelf: true });
  }

  private getValidators(fieldName: string, required: boolean) {
    let validators: any[] = [];
    switch (fieldName) {
      case 'customerName':
      case 'supplierName':
      case 'billingFrom':
      case 'cnFrom':
      case 'dnFrom':
        validators = [Validators.maxLength(250)];
        break;
      case 'registrationNo':
      case 'registrationNumber':
        validators = [Validators.maxLength(40)];
        break;
      case 'tinNumber':
      case 'tinNo':
        validators = [Validators.maxLength(14)];
        break;
      case 'country':
        validators = [Validators.maxLength(50)];
        break;
      case 'state':
        validators = [Validators.maxLength(100)];
        break;
      case 'zipCode':
        validators = [Validators.maxLength(20)];
        break;
      case 'city':
        validators = [Validators.maxLength(50)];
        break;
      case 'billingAddress':
        validators = [Validators.maxLength(400)];
        break;
      case 'customerPhone':
      case 'phoneNo':
        validators = [Validators.maxLength(100)];
        break;
      case 'classification':
        validators = [];
        break;
      default:
        break;
    }

    if (required) validators.push(Validators.required);

    return validators;
  }
}
